import { useMemo, useState } from "react";
import { type LoginCallDTO } from "~/features/cms/types/dto";
import { Card } from "~/features/configurator/Linear/components/Card";
import classNames from "classnames";
import { MediasLogo, type MediasVariant } from "~/features/navigation/components/TopBar/MediasLogo";
import { Button, Divider } from "~/common/mui";
import { LinkButton } from "~/common/components/Link";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { REGISTER_PAGE } from "~/utils/url/staticUrls";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { isForcedLogin } from "~/features/auth/login/loginUtils";
import LoginPopup from "~/features/auth/login/components/LoginPopup";
import ResetPasswordPopup from "~/features/auth/login/components/ResetPasswordPopup";
import { createMarkup } from "~/features/cms/utils/markup";
import { userIsLoggedIn } from "~/domains/user/utils/roles";
import { useTranslation } from "react-i18next";
import PersonIcon from "@mui/icons-material/Person";

export interface CMSLoginCallProps extends Omit<LoginCallDTO, "type"> {
  className?: string;
}

export const CMSLoginCall = (props: CMSLoginCallProps) => {
  const mediasLogoVariant: MediasVariant = props.accessLevel.toLowerCase() as MediasVariant;
  const { t } = useTranslation();
  const isDesktop = useIsMinDesktopSize();

  const { val: loginPopupOpen, setTrue: openLogin, setFalse: closeLogin } = useBooleanState(false);
  const { val: resetPopupOpen, setTrue: openReset, setFalse: closeReset } = useBooleanState(false);
  const [email, setEmail] = useState("");

  const onResetClick = (email: string) => {
    setEmail(email);
    closeLogin();
    openReset();
  };

  const content = useMemo(() => createMarkup(props.content), [props.content]);

  if (userIsLoggedIn()) {
    return null;
  }

  return (
    <Card className={classNames(props.className, "scha__login-call")} disableHover={true}>
      <MediasLogo className={"scha__login-call__logo"} variant={mediasLogoVariant} />
      <Divider orientation={isDesktop ? "vertical" : "horizontal"} flexItem={true} />
      <div className={"scha__login-call__content"} dangerouslySetInnerHTML={content} />
      <div className={"scha__login-call__actions"}>
        <Button
          data-test-id={"register-free"}
          href={REGISTER_PAGE}
          className={"scha__login-call__register button"}
          size={"medium"}
          startIcon={<PersonIcon  />}
        >
          {t("page.registerFree.register")}
        </Button>
        <span>{t("common.or")} </span>
        <LinkButton data-test-id={"sign-in"} onClick={openLogin}>
          {t("header.link.signIn")}
        </LinkButton>
      </div>
      {(loginPopupOpen || (isForcedLogin() && !resetPopupOpen)) && (
        <LoginPopup initialEmail={""} onResetClick={onResetClick} close={closeLogin} />
      )}
      {resetPopupOpen && <ResetPasswordPopup initialEmail={email} close={closeReset} />}
    </Card>
  );
};
